import { useState, type FormEvent, type ReactNode } from 'react';
import { api, errorMessage } from '../api';
import type { OkResponse } from '../types';
import { Modal } from './Modal';

export interface PasswordDialogProps {
  onClose: () => void;
  onSuccess: (message: string) => void;
}

/** Eigenes Passwort ändern. */
export function PasswordDialog({ onClose, onSuccess }: PasswordDialogProps): ReactNode {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [repeat, setRepeat] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    if (busy) return;
    if (newPassword.length < 8) {
      setError('Das neue Passwort muss mindestens 8 Zeichen haben.');
      return;
    }
    if (newPassword !== repeat) {
      setError('Die beiden neuen Passwörter stimmen nicht überein.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.post<OkResponse>('/api/auth/password', { currentPassword, newPassword });
      onSuccess('Passwort geändert.');
      onClose();
    } catch (cause: unknown) {
      setError(errorMessage(cause));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Passwort ändern" onClose={onClose}>
      <form id="password-form" className="form-grid" onSubmit={(event) => void submit(event)}>
        <label className="field">
          <span>Aktuelles Passwort</span>
          <input
            type="password"
            value={currentPassword}
            autoComplete="current-password"
            autoFocus
            onChange={(event) => setCurrentPassword(event.target.value)}
          />
        </label>
        <label className="field">
          <span>Neues Passwort (min. 8 Zeichen)</span>
          <input
            type="password"
            value={newPassword}
            autoComplete="new-password"
            onChange={(event) => setNewPassword(event.target.value)}
          />
        </label>
        <label className="field">
          <span>Neues Passwort wiederholen</span>
          <input
            type="password"
            value={repeat}
            autoComplete="new-password"
            onChange={(event) => setRepeat(event.target.value)}
          />
        </label>

        {error !== null && (
          <div className="feedback feedback-error" role="alert">
            <span className="feedback-icon" aria-hidden="true">
              !
            </span>
            <span>{error}</span>
          </div>
        )}

        <div className="form-actions">
          <button type="button" className="button button-ghost" onClick={onClose}>
            Abbrechen
          </button>
          <button type="submit" className="button button-primary" disabled={busy}>
            {busy ? 'Speichern …' : 'Passwort speichern'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
